import { useTranslation } from "react-i18next";

import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface DataTablePaginationProps {
  pageIndex: number;
  pageSize: number;
  pageCount: number;
  totalCount: number;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

function buildPageList(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const pages = new Set<number>([0, total - 1, current]);
  for (const d of [-1, 1]) {
    const n = current + d;
    if (n >= 0 && n < total) pages.add(n);
  }
  const sorted = Array.from(pages).sort((a, b) => a - b);
  const out: (number | "…")[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("…");
    out.push(sorted[i]);
  }
  return out;
}

export function DataTablePagination({
  pageIndex,
  pageSize,
  pageCount,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
}: DataTablePaginationProps) {
  const { t } = useTranslation();
  const from = totalCount === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min((pageIndex + 1) * pageSize, totalCount);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 pt-3">
      <div className="text-muted-foreground text-sm">
        {t("components.dataTable.pagination.showingRange", {
          from,
          to,
          total: totalCount,
        })}
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Pagination className="m-0 w-auto">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => pageIndex > 0 && onPageChange(pageIndex - 1)}
                aria-disabled={pageIndex === 0}
              />
            </PaginationItem>
            {buildPageList(pageIndex, pageCount).map((p, i) =>
              p === "…" ? (
                <PaginationItem key={`gap-${i}`}>
                  <span className="text-muted-foreground px-2">…</span>
                </PaginationItem>
              ) : (
                <PaginationItem key={p}>
                  <PaginationLink
                    isActive={p === pageIndex}
                    onClick={() => onPageChange(p)}
                  >
                    {p + 1}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}
            <PaginationItem>
              <PaginationNext
                onClick={() =>
                  pageIndex + 1 < pageCount && onPageChange(pageIndex + 1)
                }
                aria-disabled={pageIndex + 1 >= pageCount}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
        <Select
          value={String(pageSize)}
          onValueChange={(v) => onPageSizeChange(Number(v))}
        >
          <SelectTrigger className="h-8 w-[110px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((n) => (
              <SelectItem key={n} value={String(n)}>
                {t("components.dataTable.pagination.perPage", { count: n })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
